import React, { useState, useRef, useMemo, useEffect, useContext } from "react";
import useObtenerSubsecciones from "../js/translations/useObtenerSubsecciones";
import { SearchItems } from "./search-item";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useUserBooks } from "../hooks/useUserBooks";
import { BaseModal } from "../components/modals/base-modal";
import { store } from "../components/Store";

/**
 * Componente de filtro para la búsqueda. Permite al usuario aplicar filtros y actualizar el estado de los mismos.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.filterAndSearchManager  - referencia al hook useFilterAndSearchManager para poder gestionar los filtros.
 * @param {boolean} props.loadingData - Indica si los datos se están cargando.
 * @returns {JSX.Element} - Elemento JSX que representa el componente Filtro.
 */
export const Filtro = React.memo(({ filterAndSearchManager, loadingData }) => {
	const { textosSubSecciones: textosBusqueda } = useObtenerSubsecciones("busqueda");
	const [showMobileFilters, setShowMobileFilters] = useState(false);
	const [booksAwarded, setBooksAwarded] = useState(false);
	const [assistedBooks, setAssistedBooks] = useState(false);
	const userBooksData = useUserBooks();
	const userBooks = userBooksData?.books;
	const searchInputRef = useRef(null);
	const disabledSearchButtons = !filterAndSearchManager.hasChanges;
	const globalState = useContext(store);
  	const country =  globalState?.state?.pais?.toLowerCase();

	const multiCheckboxFilters = useMemo(() => {
		const filters = filterAndSearchManager?.filters;
		const result = Object.entries(filters).filter(
			(fil) => fil[1].displayType === "multicheckbox"
		);
		if (result.length > 0) {
			return result;
		}
		return [];
	}, [filterAndSearchManager.filters]);

	const singleCheckboxFilters = useMemo(() => {
		const filters = filterAndSearchManager?.filters;
		const result = Object.entries(filters).filter((fil) => fil[1].displayType === "checkbox");
		if (result.length > 0) {
			return result;
		}
		return [];
	}, [filterAndSearchManager.filters]);

	const cleanFilters = () => {
		filterAndSearchManager.deleteAllFilters();
		setShowMobileFilters(false);
	};

	const changeFilter = (property, data) => {
		filterAndSearchManager.updateFilters((draft) => {
			draft[property] = data;
		});
	};

	const search = async () => {
		filterAndSearchManager.updateUrlParams({ resetPage: true });
		filterAndSearchManager.search();
		setShowMobileFilters(false);
	};

	const updateSearchFilter = ({ value, active }) => {
		filterAndSearchManager.updateFilters((draft) => {
			draft.sch.paramValue = value;
			draft.sch.active = active;
		});
	};

	const closeMobileModal = () => {
		setShowMobileFilters(false);
	};

	useEffect(() => {
		if (userBooks) {
			const hasAwardedBooks = userBooks?.some(
				(book) => book.hasOwnProperty("libroPremiado") && book.libroPremiado === 1
			);
			const hasAssistedBooks = userBooks?.some(
				(book) => book.hasOwnProperty("libroAccesible") && book.libroPremiado === 1
			);
			setBooksAwarded(hasAwardedBooks);
			setAssistedBooks(hasAssistedBooks);
		}
	}, [userBooks]);

	const filtersContent = () => {
		return (
			<div className="filters-content">
				<form
					className="menu"
					onSubmit={(e) => {
						e.preventDefault();
					}}
				>
					<label className="search-input-wrapper">
						<input
							ref={searchInputRef}
							className="focusable-primary search-input"
							type="text"
							placeholder={textosBusqueda?.filtro?.buscar || "Buscar"}
							aria-label="Search"
							id="search-item--bar"
							value={filterAndSearchManager?.filters?.sch?.paramValue}
							onChange={(e) =>
								updateSearchFilter({
									value: e.target.value,
									active: e.target.value?.trim()?.length > 0,
								})
							}
						/>
						<button
							className="search-button"
							type="submit"
							name="Buscar"
							aria-label="Buscar"
							disabled={disabledSearchButtons}
							onClick={() => search()}
						>
							<Icon icon="fa:search" className="icon-search-button" />
						</button>
					</label>

					<div className="d-flex flex-column py-3">
						<button
							type="submit"
							className="send-search-button focusable-red"
							id="send-search"
							onClick={() => search()}
							disabled={disabledSearchButtons}
						>
							{Boolean(loadingData) === true ? (
								<i className="fas fa-spinner fa-pulse"></i>
							) : (
								<span>
									{textosBusqueda?.filtro?.aplicar_filtro || "Aplicar filtros"}
								</span>
							)}
						</button>
						{filterAndSearchManager.hasFilters && (
							<button
								type="button"
								name="Borrar filtros"
								className="remove-filters-button mt-3"
								onClick={() => cleanFilters()}
							>
								<span className="mr-1">
									{textosBusqueda?.filtro?.borrar_filtros ||
										"Borrar todos los filtros"}
								</span>
							</button>
						)}
					</div>

					<ul className="search-menu p-0 w-100">
						{multiCheckboxFilters.map((filter, i) => {
							return (
								<>
									{country !== "us" && filter[0] === "dificultadLectora" ?
										null
										:
										<SearchItems.CheckBoxSelector
											key={i}
											itemData={filter[1]}
											onChange={(data) => changeFilter(filter[0], data)}
										/>
									}
								</>
							);
						})}

						{singleCheckboxFilters.map((filter, i) => {
							const key = filter[0];
							const value = filter[1];
							if (key === "libroPremiado" && booksAwarded) {
								return (
									<li className="search-item" key={i}>
										<SearchItems.CheckBox
											itemData={value}
											onChange={(data) => {
												changeFilter(key, data);
											}}
										/>
									</li>
								);
							}

							if (key === "libroAccesible" && assistedBooks) {
								return (
									<li className="search-item" key={i}>
										<SearchItems.CheckBox
											itemData={value}
											onChange={(data) => changeFilter(key, data)}
										/>
									</li>
								);
							}

							if (key === "actividad") {
								return (
									<li className="search-item" key={i}>
										<SearchItems.CheckBox
											itemData={value}
											onChange={(data) => changeFilter(key, data)}
										/>
									</li>
								);
							}

							if (key === "bilingue") {
								return (
									<li className="search-item" key={i}>
										<SearchItems.CheckBox
											itemData={value}
											onChange={(data) => changeFilter(key, data)}
										/>
									</li>
								);
							}
						})}
					</ul>
				</form>
			</div>
		);
	};

	return (
		<>
			<div className={`desktop-filters-container px-4 px-md-0`}>
				<button
					type="button"
					className="focusable-primary primary-button-var2 filters-mobile-button align-items-center justify-content-center"
					onClick={() => {
						setShowMobileFilters(!showMobileFilters);
					}}
				>
					<span>
						<i className="fas fa-filter fa-sm"></i>{" "}
						{textosBusqueda?.filtro?.boton_filtros || "Filtros"}{" "}
						<span className="numFiltros"></span>
					</span>

					{/* <i
							className="fa fa-caret-down flecha"
							style={{ rotate: openedOnMobile ? "180deg" : "0deg" }}
						></i> */}
				</button>
				{filtersContent()}
			</div>

			<BaseModal
				windowClassName="mobile-filters-modal-window p-0"
				overlayClassName="mobile-filters-modal-overlay"
				visible={showMobileFilters}
				setVisible={setShowMobileFilters}
				backdropClose={true}
			>
				<div
					className="w-100 d-flex flex-column p-4 overflow-auto"
					style={{ overflow: "auto", maxHeight: "100%", height: "100%" }}
				>
					<div
						className="d-flex justify-content-between align-items-center mb-3 pb-4"
						style={{ borderBottom: "solid 2px var(--onbackground-fourth)" }}
					>
						<p className="font-weight-bold onbackground-primary--color text-18">
							{textosBusqueda?.filtro?.boton_filtros || "Filtros"}
						</p>
						<button
							type="button"
							className="focusable-primary primary-button-var2 px-3 py-2"
							onClick={closeMobileModal}
						>
							{textosBusqueda?.filtro?.cerrar_modal_filtros || "Cerrar"}{" "}
						</button>
					</div>
					{filtersContent()}
				</div>
			</BaseModal>
		</>
	);
});
